import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useScrollSnap } from '../hooks/useScrollSnap';
import {
  IonContent,
  IonButton,
  IonIcon,
  IonList,
  IonItem,
  IonLabel,
  IonBadge,
  IonRefresherContent,
  IonText,
  IonGrid,
  IonRow,
  IonCol,
  IonSkeletonText,
  IonSelect,
  IonSelectOption,
  IonSearchbar,
  IonPopover,
  IonFab,
  IonAvatar,
} from '@ionic/react';
import { Keyboard } from '@capacitor/keyboard';
import { KEYBOARD_GAP_PX } from '../hooks/useKeyboardHeight';
import {
  close,
  calendar,
  time,
  checkmarkCircle,
  alertCircle,
  closeCircle,
  searchOutline,
  swapVertical,
  closeCircleOutline,
  closeOutline,
} from 'ionicons/icons';
import styles from './BookingsListModal.module.scss';
import { getDefaultAvatar, proxyImage } from '@rsmaids/shared/utils/avatarUtils';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import { DataStorageService } from '@rsmaids/shared/services/DataStorageService';
import { BookingItem } from '@rsmaids/shared/types/bookings';
import BasePageWithHeader, { TitleCard } from './BasePageWithHeader';
import PositionedRefresher from './PositionedRefresher';
import { applySearchOverlayPosition } from '../utils/overlayPosition';
import { useAuth } from '../contexts';
import { useTranslation } from 'react-i18next';
import { intlLocale } from '../i18n/locale';

interface JobsListModalProps {
  onClose: () => void;
}

const JobsListModal: React.FC<JobsListModalProps> = ({ onClose }) => {
  const { t: tr } = useTranslation();
  const { authState } = useAuth();
  const { user } = authState;
  const staffName = user?.name || user?.firstName || '';

  const [searchText, setSearchText] = useState('');
  const [bookings, setBookings] = useState<BookingItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [filterValue, setFilterValue] = useState<string>('all');
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [isSearchClosing, setIsSearchClosing] = useState(false);
  const [isSearchHidden, setIsSearchHidden] = useState(false);
  const [sortBy, setSortBy] = useState<'date-newest' | 'date-oldest' | 'name-az' | 'name-za'>('date-newest');
  const [showSortPopover, setShowSortPopover] = useState(false);
  const [sortPopoverEvent, setSortPopoverEvent] = useState<Event | undefined>(undefined);
  const [showCustomDatePopover, setShowCustomDatePopover] = useState(false);
  const [customStartDate, setCustomStartDate] = useState<string | undefined>(undefined);
  const [customEndDate, setCustomEndDate] = useState<string | undefined>(undefined);
  const titleCardRef = useRef<HTMLDivElement>(null);
  const ionContentRef = useRef<HTMLIonContentElement | null>(null);
  const scrollContainerRef = useRef<HTMLIonGridElement>(null);
  const overlayRef = useRef<HTMLElement | null>(null);
  const searchRef = useRef<HTMLIonSearchbarElement | null>(null);
  const rafTick = useRef(false);
  const [refresherDisabled, setRefresherDisabled] = useState(false);
  const wasAtTop = useRef(true);

  const fetchJobs = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const dataService = DataServiceFactory.getService();
      if (!dataService) throw new Error('DataService not available');

      const today = new Date();
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(today.getDate() - 30);
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(today.getDate() + 30);

      const fetchedBookings = await dataService.bookings.getBookingsAndBlockedSlotsByDateRange(
        thirtyDaysAgo,
        thirtyDaysFromNow,
        []
      );

      if (!Array.isArray(fetchedBookings)) throw new Error('Invalid response: not an array');

      // Filter to current staff member's jobs only
      const myJobs = staffName
        ? (fetchedBookings as BookingItem[]).filter(b => b.staff && b.staff.includes(staffName))
        : (fetchedBookings as BookingItem[]);

      setBookings(myJobs);
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setError(tr('components.JobsListModal.failedToLoadJobsPleaseTry'));
      setBookings([]);
    } finally {
      setIsLoading(false);
    }
  }, [staffName, tr]);

  useEffect(() => {
    Promise.resolve().then(() => fetchJobs());
  }, [fetchJobs]);

  const uniqueServiceTypes = useMemo(() => {
    const types = new Set<string>();
    bookings.forEach(b => { if (b.serviceType?.trim()) types.add(b.serviceType); });
    return Array.from(types).sort();
  }, [bookings]);

  const processedJobs = useMemo(() => {
    let filtered = bookings.filter(b => {
      const s = searchText.toLowerCase();
      return (
        (b.contact?.name?.toLowerCase() || '').includes(s) ||
        (b.serviceType?.toLowerCase() || '').includes(s) ||
        (b.contact?.address?.toLowerCase() || '').includes(s) ||
        (b.bookingId || b.id || '').toLowerCase().includes(s)
      );
    });

    if (activeFilter !== 'all') {
      filtered = filtered.filter(b => {
        switch (activeFilter) {
          case 'session-date-time': {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const [year, month, day] = b.date.split('-').map(Number);
            const jobDate = new Date(year, month - 1, day);
            const diff = Math.round((jobDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
            switch (filterValue) {
              case 'today': return diff === 0;
              case 'tomorrow': return diff === 1;
              case 'last-7-days': return diff >= -7 && diff <= 0;
              case 'last-14-days': return diff >= -14 && diff <= 0;
              case 'last-month': return diff >= -30 && diff <= 0;
              case 'custom':
                if (!customStartDate || !customEndDate) return true;
                const start = new Date(customStartDate); start.setHours(0, 0, 0, 0);
                const end = new Date(customEndDate); end.setHours(23, 59, 59, 999);
                return jobDate >= start && jobDate <= end;
              default: return true;
            }
          }
          case 'job-status':
            if (filterValue === 'all') return true;
            return b.status?.toLowerCase() === filterValue.toLowerCase();
          case 'service-type':
            if (filterValue === 'all') return true;
            return b.serviceType === filterValue;
          default:
            return true;
        }
      });
    }

    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'date-newest': return new Date(b.startTime).getTime() - new Date(a.startTime).getTime();
        case 'date-oldest': return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
        case 'name-az': return (a.contact?.name || '').localeCompare(b.contact?.name || '');
        case 'name-za': return (b.contact?.name || '').localeCompare(a.contact?.name || '');
        default: return 0;
      }
    });
  }, [bookings, searchText, activeFilter, filterValue, sortBy, customStartDate, customEndDate]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'confirmed': return checkmarkCircle;
      case 'pending': return alertCircle;
      case 'cancelled': return closeCircle;
      default: return alertCircle;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'pending': return 'warning';
      case 'cancelled': return 'danger';
      default: return 'medium';
    }
  };

  const formatTime = (startTime: string | Date): string => {
    const date = typeof startTime === 'string' ? new Date(startTime) : startTime;
    return date.toLocaleTimeString(intlLocale(), { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const formatDate = (dateStr: string): string => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString(intlLocale(), { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const updateOverlayPosition = useCallback(() => {
    applySearchOverlayPosition(overlayRef.current, isSearchVisible);
  }, [isSearchVisible]);

  useEffect(() => {
    updateOverlayPosition();
    window.addEventListener('resize', updateOverlayPosition);
    return () => window.removeEventListener('resize', updateOverlayPosition);
  }, [updateOverlayPosition]);

  useEffect(() => {
    const id = window.setTimeout(() => updateOverlayPosition(), 30);
    return () => window.clearTimeout(id);
  }, [isSearchVisible, updateOverlayPosition]);

  useEffect(() => {
    const scrollContainerEl = scrollContainerRef.current;
    let didShowHandle: { remove: () => Promise<void> } | null = null;
    let didHideHandle: { remove: () => Promise<void> } | null = null;
    let bottomAnimRaf = 0;
    let currentBottom = 0;

    const setBottomOffset = (offset: number) => {
      const el = overlayRef.current;
      if (!el) return;
      if (offset > 0) {
        el.style.setProperty('bottom', `${offset}px`, 'important');
        el.style.removeProperty('transform');
      } else {
        el.style.removeProperty('bottom');
        el.style.removeProperty('transform');
      }
    };

    const animateBottomTo = (kbHeight: number) => {
      const targetBottom = kbHeight > 0 ? kbHeight + KEYBOARD_GAP_PX : 0;
      if (bottomAnimRaf) cancelAnimationFrame(bottomAnimRaf);

      const startBottom = currentBottom;
      const delta = targetBottom - startBottom;
      if (Math.abs(delta) < 1) {
        currentBottom = targetBottom;
        setBottomOffset(currentBottom);
        return;
      }

      const duration = targetBottom > startBottom ? 220 : 170;
      const startTime = performance.now();

      const step = (now: number) => {
        const t = Math.min((now - startTime) / duration, 1);
        const eased = 1 - Math.pow(1 - t, 3);
        currentBottom = startBottom + (delta * eased);
        setBottomOffset(currentBottom);

        if (t < 1) bottomAnimRaf = requestAnimationFrame(step);
        else {
          currentBottom = targetBottom;
          setBottomOffset(currentBottom);
        }
      };

      bottomAnimRaf = requestAnimationFrame(step);
    };

    const applyKeyboardOffset = (kbHeight: number) => {
      const grid = scrollContainerRef.current;
      if (grid) {
        if (kbHeight > 0) grid.style.paddingBottom = `${kbHeight + 60}px`;
        else grid.style.removeProperty('padding-bottom');
      }
      animateBottomTo(kbHeight);
    };

    try {
      Keyboard.addListener('keyboardWillShow', (info) => applyKeyboardOffset(info.keyboardHeight || 0))
        .then(h => { didShowHandle = h; }).catch(() => {});
      Keyboard.addListener('keyboardDidShow', (info) => applyKeyboardOffset(info.keyboardHeight || 0))
        .catch(() => {});
      Keyboard.addListener('keyboardWillHide', () => applyKeyboardOffset(0))
        .then(h => { didHideHandle = h; }).catch(() => {});
      Keyboard.addListener('keyboardDidHide', () => applyKeyboardOffset(0))
        .catch(() => {});
    } catch {}

    const vv = window.visualViewport;
    let vvRaf = 0;
    const handleViewportResize = () => {
      if (vvRaf) cancelAnimationFrame(vvRaf);
      vvRaf = requestAnimationFrame(() => {
        if (!vv) return;
        const offset = window.innerHeight - vv.height - vv.offsetTop;
        applyKeyboardOffset(offset > 50 ? offset : 0);
      });
    };
    if (vv) vv.addEventListener('resize', handleViewportResize);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') setTimeout(handleViewportResize, 300);
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      scrollContainerEl?.style.removeProperty('padding-bottom');
      const el = overlayRef.current;
      if (el) { el.style.removeProperty('transform'); el.style.removeProperty('bottom'); }
      if (bottomAnimRaf) cancelAnimationFrame(bottomAnimRaf);
      if (vvRaf) cancelAnimationFrame(vvRaf);
      if (vv) vv.removeEventListener('resize', handleViewportResize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      try { didShowHandle?.remove(); } catch {}
      try { didHideHandle?.remove(); } catch {}
    };
  }, []);

  const handleToggleSearch = () => {
    if (isSearchVisible) {
      if (searchRef.current) {
        try { (searchRef.current as HTMLIonSearchbarElement).value = ''; } catch {}
      }
      setSearchText('');
      setIsSearchClosing(true);
      setIsSearchVisible(false);
      setTimeout(() => { setIsSearchHidden(true); setIsSearchClosing(false); }, 220);
      try {
        const s = searchRef.current as HTMLIonSearchbarElement | null;
        const getter = s ? (s as unknown as { getInputElement?: () => Promise<HTMLInputElement> }).getInputElement : undefined;
        if (getter) {
          getter().then((el: HTMLInputElement) => { try { el.blur(); } catch {} try { Keyboard.hide(); } catch {} }).catch(() => { try { Keyboard.hide(); } catch {} });
        } else { try { Keyboard.hide(); } catch {} }
      } catch {}
    } else {
      setIsSearchHidden(false);
      setIsSearchVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          try {
            const s = searchRef.current as HTMLIonSearchbarElement | null;
            const getter = s ? (s as unknown as { getInputElement?: () => Promise<HTMLInputElement> }).getInputElement : undefined;
            if (getter) getter().then((el: HTMLInputElement) => { try { el.focus(); } catch {} }).catch(() => {});
          } catch {}
        });
      });
    }
  };

  const applyProgress = useCallback((progress: number, smooth: boolean) => {
    let headerEl: HTMLElement | null = null;
    const modalHost = scrollContainerRef.current?.closest('ion-modal') as HTMLElement | null;
    if (modalHost) headerEl = modalHost.querySelector<HTMLElement>('[data-header-title]');
    if (!headerEl) {
      const contentHost = scrollContainerRef.current?.closest('ion-content') as HTMLElement | null;
      headerEl = contentHost?.querySelector<HTMLElement>('[data-header-title]') ?? document.querySelector<HTMLElement>('[data-header-title]');
    }

    if (smooth) {
      if (headerEl) headerEl.style.transition = 'opacity 0.2s ease-out';
      if (titleCardRef.current) titleCardRef.current.style.transition = 'opacity 0.2s ease-out, transform 0.2s ease-out';
      setTimeout(() => {
        if (headerEl) headerEl.style.transition = '';
        if (titleCardRef.current) titleCardRef.current.style.transition = '';
      }, 220);
    }

    headerEl?.style.setProperty('--title-opacity', String(progress));
    if (titleCardRef.current) {
      titleCardRef.current.style.setProperty('--content-opacity', String(1 - progress));
      titleCardRef.current.style.setProperty('--content-scale', `scale(${1 - (progress * 0.2)})`);
    }
  }, []);

  const handleScroll = useScrollSnap(scrollContainerRef, 66, applyProgress);

  return (
    <div className={styles.modalWrapper}>
      <BasePageWithHeader
        title={tr('components.JobsListModal.myJobs')}
        toolbarClassName={styles.toolbar}
        smallTitleClassName={styles.smallTitle}
        titleHeaderClassName={styles.titleHeader}
        startButtons={
          <IonButton shape="round" className={styles.closeButton} onClick={onClose}>
            <IonIcon icon={closeCircleOutline} />
          </IonButton>
        }
        endButtons={
          <IonButton shape="round" className={styles.searchButton} onClick={handleToggleSearch}>
            <IonIcon icon={isSearchVisible ? closeOutline : searchOutline} />
          </IonButton>
        }
      >
        <IonContent ref={ionContentRef} onIonScroll={(e) => handleScroll(e.detail.scrollTop)}>
          <PositionedRefresher
            titleRef={titleCardRef}
            contentRef={scrollContainerRef}
            offset={0}
            anchor="bottom"
            id="jobs-refresher"
            className={styles.bookingsRefresher}
            pullMin={120}
            pullMax={200}
            pullFactor={0.6}
            disabled={refresherDisabled}
            onIonRefresh={(e) => {
              setIsLoading(true);
              setBookings([]);
              const loadJobs = async () => {
                try {
                  const dataService = DataServiceFactory.getService();
                  if (!dataService) throw new Error('DataService not available');

                  const today = new Date();
                  const thirtyDaysAgo = new Date();
                  thirtyDaysAgo.setDate(today.getDate() - 30);
                  const thirtyDaysFromNow = new Date();
                  thirtyDaysFromNow.setDate(today.getDate() + 30);

                  const fetched = await dataService.bookings.getBookingsAndBlockedSlotsByDateRange(
                    thirtyDaysAgo, thirtyDaysFromNow, []
                  );

                  if (!Array.isArray(fetched)) throw new Error('Invalid response');

                  const myJobs = staffName
                    ? (fetched as BookingItem[]).filter(b => b.staff && b.staff.includes(staffName))
                    : (fetched as BookingItem[]);

                  setBookings(myJobs);
                } catch (err) {
                  console.error('Error refreshing jobs:', err);
                } finally {
                  setIsLoading(false);
                  e.detail.complete();
                }
              };
              loadJobs();
            }}
            style={{
              '--ion-color-step-250': 'var(--ion-background-color)',
              '--ion-color-step-200': 'var(--ion-background-color)',
              '--ion-background-color': 'transparent',
            } as React.CSSProperties}
          >
            <IonRefresherContent pullingIcon="bubbles" refreshingSpinner="bubbles" />
          </PositionedRefresher>

          <IonGrid
            ref={scrollContainerRef}
            className={styles.bookingListContainer}
            onScroll={(e) => {
              const top = (e.target as HTMLElement).scrollTop || 0;
              const atTop = top <= 5;
              if (atTop !== wasAtTop.current) {
                wasAtTop.current = atTop;
                setRefresherDisabled(!atTop);
              }
              if (!rafTick.current) {
                rafTick.current = true;
                requestAnimationFrame(() => { handleScroll(top); rafTick.current = false; });
              }
            }}
          >
            <div ref={titleCardRef} className={styles.titleCardWrapper}>
              <TitleCard
                title={tr('components.JobsListModal.myJobs')}
                cardClassName={styles.bookingsTitleCard}
                titleContainerClassName={styles.titleContainer}
                titleClassName={styles.bookingsTitle}
              />
            </div>

            {/* Filter Controls */}
            <IonItem className={styles.filterControls}>
              <IonSelect
                interface="alert"
                interfaceOptions={{ cssClass: 'customAlert', backdropDismiss: true, header: tr('components.JobsListModal.filterBy') }}
                value={activeFilter}
                onIonChange={(e) => { setActiveFilter(e.detail.value); setFilterValue('all'); }}
                className={styles.sortSelect}
              >
                <IonSelectOption value="all">{tr('components.JobsListModal.allJobs')}</IonSelectOption>
                <IonSelectOption value="session-date-time">{tr('components.JobsListModal.jobDateTime')}</IonSelectOption>
                <IonSelectOption value="job-status">{tr('components.JobsListModal.jobStatus')}</IonSelectOption>
                <IonSelectOption value="service-type">{tr('components.JobsListModal.serviceType')}</IonSelectOption>
              </IonSelect>

              {activeFilter !== 'all' && (
                <IonSelect
                  interface="alert"
                  interfaceOptions={{
                    cssClass: 'customAlert',
                    backdropDismiss: true,
                    header: activeFilter === 'session-date-time' ? tr('components.JobsListModal.jobDateTime2') :
                            activeFilter === 'job-status' ? tr('components.JobsListModal.jobStatus2') :
                            activeFilter === 'service-type' ? tr('components.JobsListModal.serviceType2') : tr('components.JobsListModal.selectFilter'),
                  }}
                  value={filterValue}
                  onIonChange={(e) => {
                    const val = e.detail.value;
                    setFilterValue(val);
                    if (val === 'custom' && activeFilter === 'session-date-time') {
                      setTimeout(() => setShowCustomDatePopover(true), 300);
                    }
                  }}
                  className={styles.filterValueSelect}
                >
                  {activeFilter === 'session-date-time' && (
                    <>
                      <IonSelectOption value="all">{tr('components.JobsListModal.all')}</IonSelectOption>
                      <IonSelectOption value="today">{tr('components.JobsListModal.today')}</IonSelectOption>
                      <IonSelectOption value="tomorrow">{tr('components.JobsListModal.tomorrow')}</IonSelectOption>
                      <IonSelectOption value="last-7-days">{tr('components.JobsListModal.last7Days')}</IonSelectOption>
                      <IonSelectOption value="last-14-days">{tr('components.JobsListModal.last14Days')}</IonSelectOption>
                      <IonSelectOption value="last-month">{tr('components.JobsListModal.lastMonth')}</IonSelectOption>
                      <IonSelectOption value="custom">{tr('components.JobsListModal.custom')}</IonSelectOption>
                    </>
                  )}
                  {activeFilter === 'job-status' && (
                    <>
                      <IonSelectOption value="all">{tr('components.JobsListModal.allStatuses')}</IonSelectOption>
                      <IonSelectOption value="confirmed">{tr('components.JobsListModal.confirmed')}</IonSelectOption>
                      <IonSelectOption value="pending">{tr('components.JobsListModal.pending')}</IonSelectOption>
                      <IonSelectOption value="completed">{tr('components.JobsListModal.completed')}</IonSelectOption>
                      <IonSelectOption value="cancelled">{tr('components.JobsListModal.cancelled')}</IonSelectOption>
                    </>
                  )}
                  {activeFilter === 'service-type' && (
                    <>
                      <IonSelectOption value="all">{tr('components.JobsListModal.all')}</IonSelectOption>
                      {uniqueServiceTypes.map(t => (
                        <IonSelectOption key={t} value={t}>{t}</IonSelectOption>
                      ))}
                    </>
                  )}
                </IonSelect>
              )}

              <IonButton
                fill="clear"
                shape="round"
                expand="block"
                onClick={(e) => { setSortPopoverEvent(e.nativeEvent); setShowSortPopover(true); }}
                className={styles.sortOrderButton}
                slot="end"
              >
                <IonIcon icon={swapVertical} />
              </IonButton>

              <IonPopover
                isOpen={showSortPopover}
                event={sortPopoverEvent}
                onDidDismiss={() => setShowSortPopover(false)}
                className={styles.sortPopover}
              >
                <IonList>
                  <IonItem button detail={false} lines="none" onClick={() => { setSortBy('date-newest'); setShowSortPopover(false); }} className={sortBy === 'date-newest' ? styles.activeSort : ''}>
                    <IonLabel>{tr('components.JobsListModal.dateNewestFirst')}</IonLabel>
                  </IonItem>
                  <IonItem button detail={false} lines="none" onClick={() => { setSortBy('date-oldest'); setShowSortPopover(false); }} className={sortBy === 'date-oldest' ? styles.activeSort : ''}>
                    <IonLabel>{tr('components.JobsListModal.dateOldestFirst')}</IonLabel>
                  </IonItem>
                  <IonItem button detail={false} lines="none" onClick={() => { setSortBy('name-az'); setShowSortPopover(false); }} className={sortBy === 'name-az' ? styles.activeSort : ''}>
                    <IonLabel>{tr('components.JobsListModal.nameAZ')}</IonLabel>
                  </IonItem>
                  <IonItem button detail={false} lines="none" onClick={() => { setSortBy('name-za'); setShowSortPopover(false); }} className={sortBy === 'name-za' ? styles.activeSort : ''}>
                    <IonLabel>{tr('components.JobsListModal.nameZA')}</IonLabel>
                  </IonItem>
                </IonList>
              </IonPopover>
            </IonItem>

            {isLoading ? (
              <IonList className={styles.bookingList}>
                {Array.from({ length: 5 }).map((_, idx) => (
                  <IonItem lines="none" key={`skeleton-${idx}`} className={styles.bookingItem}>
                    <IonSkeletonText animated style={{ width: '40px', height: '40px', borderRadius: '50%' }} slot="start" />
                    <IonLabel>
                      <IonSkeletonText animated style={{ width: '150px', height: '20px' }} />
                      <p><IonSkeletonText animated style={{ width: '100px', height: '16px' }} /></p>
                      <div className={styles.bookingDetails}>
                        <IonGrid>
                          <IonRow>
                            <IonCol size="6"><IonSkeletonText animated style={{ width: '80px', height: '14px' }} /></IonCol>
                            <IonCol size="6"><IonSkeletonText animated style={{ width: '70px', height: '14px' }} /></IonCol>
                          </IonRow>
                          <IonRow><IonCol><IonSkeletonText animated style={{ width: '120px', height: '12px' }} /></IonCol></IonRow>
                        </IonGrid>
                      </div>
                    </IonLabel>
                    <div className={styles.badgeContainer}>
                      <IonSkeletonText animated style={{ width: '70px', height: '28px', borderRadius: '16px' }} />
                    </div>
                  </IonItem>
                ))}
              </IonList>
            ) : error ? (
              <div className={styles.emptyState}>
                <IonText color="danger"><p>{error}</p></IonText>
              </div>
            ) : (
              <IonList className={styles.bookingList}>
                {processedJobs.map((job) => (
                  <IonItem
                    key={job.id}
                    className={styles.bookingItem}
                    button
                    detail={false}
                    onClick={() => {
                      DataStorageService.storeData(`booking-${job.id}`, job);
                      DataStorageService.storeData('open-booking-details-modal', job.id);
                    }}
                  >
                    <IonAvatar slot="start" className={styles.clientIcon}>
                      <img
                        src={job.contact?.avatarUrl ? proxyImage(job.contact.avatarUrl) : getDefaultAvatar(job.contact?.name || '')}
                        alt={job.contact?.name || ''}
                        onError={(e) => { (e.target as HTMLImageElement).src = getDefaultAvatar(job.contact?.name || ''); }}
                      />
                    </IonAvatar>
                    <IonLabel>
                      <h2 className={styles.clientName}>{job.contact?.name || tr('components.JobsListModal.unknownClient')}</h2>
                      <p className={styles.service}>{job.serviceType || tr('components.JobsListModal.service')}</p>
                      <div className={styles.bookingDetails}>
                        <IonGrid>
                          <IonRow>
                            <IonCol size="6">
                              <div className={styles.detailItem}>
                                <IonIcon icon={calendar} />
                                <span>{formatDate(job.date)}</span>
                              </div>
                            </IonCol>
                            <IonCol size="6">
                              <div className={styles.detailItem}>
                                <IonIcon icon={time} />
                                <span>{formatTime(job.startTime)}</span>
                              </div>
                            </IonCol>
                          </IonRow>
                          <IonRow>
                            <IonCol>
                              <div className={styles.address}>
                                <IonText color="medium">
                                  <small>{job.contact?.address || tr('components.JobsListModal.noAddress')}</small>
                                </IonText>
                              </div>
                            </IonCol>
                          </IonRow>
                        </IonGrid>
                      </div>
                    </IonLabel>
                    <div className={styles.badgeContainer}>
                      <IonBadge
                        color={getStatusColor(job.status)}
                        className={`${styles.statusBadge} ${styles[job.status.toLowerCase()]}`}
                      >
                        <IonIcon icon={getStatusIcon(job.status)} />
                        {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                      </IonBadge>
                    </div>
                  </IonItem>
                ))}
              </IonList>
            )}

            {!isLoading && !error && processedJobs.length === 0 && (
              <div className={styles.emptyState}>
                <IonText color="medium"><p>{tr('components.JobsListModal.noJobsFound')}</p></IonText>
              </div>
            )}

            {isSearchVisible && <div className={styles.searchBarSpacer} />}
          </IonGrid>
        </IonContent>

        <IonFab
          ref={el => { overlayRef.current = el; }}
          className={`${styles.searchMorph} ${isSearchVisible ? styles.searchMorphExpanded : ''} ${isSearchClosing ? styles.searchMorphClosing : ''} ${isSearchHidden ? styles.hidden : ''}`}
          aria-hidden={!isSearchVisible}
        >
          <IonSearchbar
            ref={searchRef}
            value={searchText}
            debounce={50}
            onIonInput={e => setSearchText(e.detail.value ?? '')}
            onIonClear={() => setSearchText('')}
            placeholder={tr('components.JobsListModal.searchJobs', { length: bookings.length })}
            animated
            showCancelButton="never"
            showClearButton={searchText ? 'always' : 'never'}
            className={`${styles.searchBar} ${styles.floatingSearchBarInline} ${isSearchVisible ? styles.floatingSearchBarInlineExpanded : ''}`}
          />
        </IonFab>
      </BasePageWithHeader>

      {/* Custom Date Range Popover */}
      <IonPopover
        isOpen={showCustomDatePopover}
        onDidDismiss={() => setShowCustomDatePopover(false)}
        className={styles.customDatePopover}
        backdropDismiss={true}
      >
        <div className={styles.popoverContent}>
          <div className={styles.popoverHeader}>
            <h3>{tr('components.JobsListModal.selectDateRange')}</h3>
            <IonButton fill="clear" size="small" onClick={() => setShowCustomDatePopover(false)}>
              <IonIcon icon={close} />
            </IonButton>
          </div>
          <div className={styles.datePickerContainer}>
            <div className={styles.datePickerSection}>
              <IonLabel className={styles.dateLabel}>{tr('components.JobsListModal.startDate')}</IonLabel>
              <input
                type="date"
                value={customStartDate?.split('T')[0] || ''}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className={styles.nativeDatePicker}
                aria-label={tr('components.JobsListModal.startDate')}
              />
            </div>
            <div className={styles.datePickerSection}>
              <IonLabel className={styles.dateLabel}>{tr('components.JobsListModal.endDate')}</IonLabel>
              <input
                type="date"
                value={customEndDate?.split('T')[0] || ''}
                onChange={(e) => setCustomEndDate(e.target.value)}
                min={customStartDate?.split('T')[0] || ''}
                className={styles.nativeDatePicker}
                aria-label={tr('components.JobsListModal.endDate')}
              />
            </div>
          </div>
          <div className={styles.popoverActions}>
            <IonButton
              fill="clear"
              color="primary"
              onClick={() => setShowCustomDatePopover(false)}
              disabled={!customStartDate || !customEndDate}
            >{tr('components.JobsListModal.applyDateRange')}
            </IonButton>
          </div>
        </div>
      </IonPopover>
    </div>
  );
};

export default JobsListModal;
